
/* eslint-disable max-classes-per-file */
import { isNil } from "lodash";
import { DigitalSignaturDeltagareTyper, DigitalSignaturStatus, VirkeskopAffarsformer, VirkeskopKontraktstatus } from "~app/common/models/constants";
import { Period } from "~app/common/models/period";
import { RuleValidationMessage } from "~app/common/services/cqs/models/rule-validation-message";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IKartgeometri } from "~app/features/map/popups/kartgeometri-popup/kartgeometri.model";
import { Atgard } from "./atgard-models";
import { VKKontraktUtskriftDTO } from "./utskrift/utskrift-models";

export enum VacsKontraktstatus {
    KontraktInlast = 1,
    Betaln = 2,
    Avraknat = 5,
    Avslutat = 6,
    Raderat = 99
}

export class VKKontraktDTO {
    id?: number;
    kontraktTyp: string;
    taxerasSomLantbruksenhet: boolean;
    guid: string;
    rowVersion: string;
    benamning: string;
    externBeteckning: string;
    anskaffningsform: string;
    affarsform: string;
    anbudGiltighetFrom?: string;
    anbudGiltighetTom?: string;
    fastighetsbeteckning: string;
    markagare: string;
    markagareTelefon: string;
    markagareEpost: string;
    kontraktnummer?: string;
    kontraktdatum: string;
    kontraktGiltigtFrom?: string;
    kontraktGiltigtTom?: string;
    lkd: string;
    nettotyp?: string;
    centrumkoordinat: string;
    skordarmatning?: boolean;
    ranta?: boolean;
    anteckningAvverkningsdirektiv: string;
    anteckningTransportorder: string;
    anteckningIntern: string;
    status: string;
    kartgeometrier: IKartgeometri[];
    atgarder: Atgard[];
    vacsKontraktAvslutat: boolean;
    skickadTillVacs?: string;
    viol3: boolean;
    hasArbetsorderWhereProduktionsplaneringPagar: boolean;
    anteckningPlanerare: string;
    kontaktaPlaneraren: boolean;
    validationMessages: RuleValidationMessage[];
    vacsKontraktstatus: VacsKontraktstatus | -1;
    leverantorsOrganisationExternKod: string;
    kontraktKopieratFranId?: number;
    kontraktKopieratFranBeteckning?: string;
    kontraktKopieratFranNummer?: string;
    vacsEvents: VKVacsEventsDTO;
    relateradeKontrakt: VKReleateratKontraktResponseDTO[];
    anteckningSkogsvard?: string;
    hasAktivSignering: boolean;
    legacySkogsvardBetalraderLocked: boolean;
}

export class VKVacsEventsDTO {
    skickadTillVacs?: Date;
    vacsAvraknatDatum?: Date;
    vacsAvslutatDatum?: Date;
    vacsBetalningInlagdDatum: Date;
    vacsInlastDatum: Date;
}

export class VKReleateratKontraktResponseDTO {
    id: number;
    benamning: string;
    kontraktnummer: string;
}

export class Kontrakt extends VKKontraktDTO {

    get hasAffarsform(): boolean {
        return ObjectUtils.hasValue(this.affarsform);
    }

    get isGrot(): boolean {
        return this.kontraktTyp === VirkeskopKontraktstyp.Grot;
    }

    get isRundvirke(): boolean {
        return this.kontraktTyp === VirkeskopKontraktstyp.Rundvirke;
    }

    get isRotpost(): boolean {
        return this.affarsform === VirkeskopAffarsformer.Rotpost;
    }

    get isLeveransvirke(): boolean {
        return this.affarsform === VirkeskopAffarsformer.Leveransvirke;
    }

    get isAffarsformU4() {
        return this.affarsform === VirkeskopAffarsformer.AuFastKostnad;
    }

    get isAffarsformU5() {
        return this.affarsform === VirkeskopAffarsformer.AuSjalvkostnad;
    }

    get isAffarsformU6() {
        return this.affarsform === VirkeskopAffarsformer.Leveransrotkop;
    }

    get showSkogsvard() {
        return this.isAnySkogsvard ||
            (this.hasAffarsform && this.isRundvirke && (this.isAffarsformU4 || this.isAffarsformU5 || this.isAffarsformU6));
    }

    get isAnbud() {
        return this.status === VirkeskopKontraktstatus.Anbud;
    }

    get isKontrakt() {
        return this.status === VirkeskopKontraktstatus.Kontrakt;
    }

    get isKontraktMakulerat() {
        return this.status === VirkeskopKontraktstatus.Makulerat;
    }

    get isNew(): boolean {
        return isNil(this.id);
    }

    get isSkogsvardsKontrakt() {
        return this.kontraktTyp === VirkeskopKontraktstyp.Skogsvard;
    }

    get isSkogsvardFPVKontrakt() {
        return this.kontraktTyp === VirkeskopKontraktstyp.SkogsvardFPV;
    }

    get isNotSkogsvardFPVKontrakt() {
        return this.kontraktTyp !== VirkeskopKontraktstyp.SkogsvardFPV;
    }

    get isAnySkogsvard() {
        return this.isSkogsvardsKontrakt || this.isSkogsvardFPVKontrakt;
    }

    get isAvverkning(): boolean {
        return this.affarsform !== VirkeskopAffarsformer.Leveransvirke;
    }

    get isVacsAvraknatOrAvslutat(): boolean {
        return this.vacsKontraktstatus === VacsKontraktstatus.Avraknat || this.vacsKontraktstatus === VacsKontraktstatus.Avslutat;
    }

    get giltighet() {
        const period = new Period();
        period.periodFrom = this.kontraktGiltigtFrom
            ? new Date(this.kontraktGiltigtFrom)
            : null;
        period.periodTom = this.kontraktGiltigtTom
            ? new Date(this.kontraktGiltigtTom)
            : null;

        return period;
    }

    constructor(guid?: string, dto?: VKKontraktDTO) {
        super();

        this.id = dto?.id;
        this.kontraktTyp = dto?.kontraktTyp ?? "Rundvirke";
        this.taxerasSomLantbruksenhet = dto?.taxerasSomLantbruksenhet ?? false;
        this.affarsform = dto?.affarsform;
        this.anskaffningsform = dto?.anskaffningsform;
        this.anbudGiltighetFrom = dto?.anbudGiltighetFrom;
        this.anbudGiltighetTom = dto?.anbudGiltighetTom;
        this.anteckningAvverkningsdirektiv = dto?.anteckningAvverkningsdirektiv ?? "";
        this.anteckningTransportorder = dto?.anteckningTransportorder ?? "";
        this.benamning = dto?.benamning ?? "";
        this.externBeteckning = dto?.externBeteckning ?? "";
        this.centrumkoordinat = dto?.centrumkoordinat ?? "";
        this.fastighetsbeteckning = dto?.fastighetsbeteckning ?? "";
        this.markagare = dto?.markagare ?? "";
        this.markagareTelefon = dto?.markagareTelefon ?? "";
        this.markagareEpost = dto?.markagareEpost ?? "";
        this.lkd = dto?.lkd ?? "";
        this.nettotyp = dto?.nettotyp;
        this.guid = dto?.guid ?? guid;
        this.rowVersion = dto?.rowVersion;
        this.kontraktnummer = dto?.kontraktnummer;
        this.kontraktGiltigtFrom = dto?.kontraktGiltigtFrom;
        this.kontraktGiltigtTom = dto?.kontraktGiltigtTom;
        this.kontraktdatum = dto?.kontraktdatum ?? "";
        this.ranta = dto?.ranta ?? false;
        this.skordarmatning = dto?.skordarmatning ?? null;
        this.status = dto?.status ?? VirkeskopKontraktstatus.Anbud;
        this.kartgeometrier = dto?.kartgeometrier;
        this.vacsKontraktAvslutat = dto?.vacsKontraktAvslutat ?? false;
        this.vacsKontraktstatus = dto?.vacsKontraktstatus ?? -1;
        this.skickadTillVacs = dto?.skickadTillVacs;
        this.viol3 = dto?.viol3;
        this.hasArbetsorderWhereProduktionsplaneringPagar = dto?.hasArbetsorderWhereProduktionsplaneringPagar;
        this.anteckningPlanerare = dto?.anteckningPlanerare ?? "";
        this.anteckningIntern = dto?.anteckningIntern ?? "";
        this.kontaktaPlaneraren = dto?.kontaktaPlaneraren ?? false;
        this.validationMessages = dto?.validationMessages;
        this.leverantorsOrganisationExternKod = dto?.leverantorsOrganisationExternKod;
        this.kontraktKopieratFranId = dto?.kontraktKopieratFranId;
        this.kontraktKopieratFranBeteckning = dto?.kontraktKopieratFranBeteckning;
        this.kontraktKopieratFranNummer = dto?.kontraktKopieratFranNummer;
        this.vacsEvents = dto?.vacsEvents;
        this.relateradeKontrakt = dto?.relateradeKontrakt;
        this.anteckningSkogsvard = dto?.anteckningSkogsvard;
        this.hasAktivSignering = dto?.hasAktivSignering;
        this.legacySkogsvardBetalraderLocked = dto?.legacySkogsvardBetalraderLocked ?? false;
    }
}

export class VirkeskopNettotyper {
    // eslint-disable-next-line sonarjs/public-static-readonly
    static Sortimentsnetto = "Sortimentsnetto";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static Snittnetto = "Snittnetto";
}

export class VirkeskopKontraktstyp {
    // eslint-disable-next-line sonarjs/public-static-readonly
    static Grot = "Grot";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static Rundvirke = "Rundvirke";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static Skogsvard = "Skogsvård";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static SkogsvardFPV = "Skogsvård SSP";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static Alla: string[] = [
        VirkeskopKontraktstyp.Rundvirke,
        VirkeskopKontraktstyp.Grot,
        VirkeskopKontraktstyp.Skogsvard,
        VirkeskopKontraktstyp.SkogsvardFPV
    ];
}

export enum RaskSigneringStatus {
    Ny = "Ny",
    SkickaPagar = "SkickaPagar",
    Skickat = "Skickat",
    KontraktNedladdat = "KontraktNedladdat",
    Aterkallad = "Aterkallad"
}

export class VirkeskopskontraktDigitalSignering {
    id: number;
    kontraktId: number;
    signingProviderStatus: string;
    status?: RaskSigneringStatus;
    signingProviderDocumentKey: string;
    skickadDokumentVersion: number;
    aktiv: boolean;
    dokumentNamn: string;
    dokumentSkapadDatum: Date;
    forfalloDatum?: Date;
    skickat: Date;
    signerat?: Date;
    bilagor: VirkeskopskontraktDigitalSigneringBilagaDTO[];
    deltagare: VirkeskopskontraktDigitalSigneringDeltagareDTO[];
    kontraktDokumentId?: number;
}

export class VirkeskopskontraktDigitalSigneringDeltagareDTO {
    id: number;
    status: string;
    signingProviderParticipantKey: string;
    ansvarigVirkeskopareId?: number;
    ansvarigVirkeskopareNamn: string;
    leverantorId?: number;
    leverantorNamn: string;
    leverantorEpost: string;
    namn: string;
    makeMakaNamn: string;
    makeMakaEpost: string;
    deltagarTyp: string;
    senastVisat?: Date;
    avvisatTidpunkt: Date;
    avvisatMeddelande: string;
    signerat: Date;
    emailDeliveryError?: string;
    smsNummer: string;
    signeringsmetod: string;
    alternativKontaktperson: boolean;
    inbjudningsOrdning: number;
}

export class VirkeskopskontraktDigitalSigneringBilagaDTO {
    namn: string;
    id: number;

    dokumentId?: number;
    dokumentVersion: number;
    skapadDatum: Date;
}

export class VKDigitalsigneringUtskrifterDTO {
    utskrifter: VKKontraktUtskriftDTO[];
    ovrigaBilagor: VKKontraktUtskriftDTO[];
}

export class VirkeskopskontraktDigitalSigneringViewModel extends VirkeskopskontraktDigitalSignering {

    override deltagare: VirkeskopskontraktDigitalSigneringDeltagareViewModel[];

    get skickatDatum() {
        return DateUtils.formatUtcAsLocal(this.skickat, DateUtils.datetimeformatWithoutMillis);
    }

    get forfalloDatumFormated() {
        return DateUtils.formatDateString(this.forfalloDatum, DateUtils.dateformat);
    }

    get isPending() {
        return this.signingProviderStatus === DigitalSignaturStatus.pending;
    }

    get hasPassedSigningDay() {
        return this.status === RaskSigneringStatus.Skickat && this.signingProviderStatus === DigitalSignaturStatus.overdue;
    }

    constructor(dto: VirkeskopskontraktDigitalSignering) {
        super();
        this.aktiv = dto?.aktiv;
        this.dokumentNamn = dto?.dokumentNamn;
        this.dokumentSkapadDatum = dto?.dokumentSkapadDatum;
        this.id = dto?.id;
        this.kontraktId = dto?.kontraktId;
        this.signerat = dto?.signerat;
        this.kontraktDokumentId = dto?.kontraktDokumentId;
        this.signingProviderDocumentKey = dto?.signingProviderDocumentKey;
        this.skickadDokumentVersion = dto?.skickadDokumentVersion;
        this.skickat = dto?.skickat;
        this.signingProviderStatus = dto?.signingProviderStatus;
        this.bilagor = dto?.bilagor;
        this.status = dto?.status;
        this.forfalloDatum = dto?.forfalloDatum;
        this.deltagare = dto?.deltagare.map(x => new VirkeskopskontraktDigitalSigneringDeltagareViewModel(x));
    }
}

export class VirkeskopskontraktDigitalSigneringDeltagareViewModel extends VirkeskopskontraktDigitalSigneringDeltagareDTO {
    get signeratDatum() {
        return DateUtils.formatUtcAsLocal(this.signerat, DateUtils.datetimeformatWithoutMillis);
    }

    get visatDatum() {
        return DateUtils.formatUtcAsLocal(this.senastVisat, DateUtils.datetimeformatWithoutMillis);
    }

    get avvisatDatum() {
        return DateUtils.formatUtcAsLocal(this.avvisatTidpunkt, DateUtils.datetimeformatWithoutMillis);
    }

    get deltagarNamn() {
        if (this.deltagarTyp === DigitalSignaturDeltagareTyper.leverantor) {
            return this.leverantorNamn;
        }
        if (this.deltagarTyp === DigitalSignaturDeltagareTyper.extraDeltagare) {
            return this.namn;
        }
        return this.deltagarTyp === DigitalSignaturDeltagareTyper.makeMaka ? this.makeMakaNamn : this.ansvarigVirkeskopareNamn;
    }

    constructor(dto: VirkeskopskontraktDigitalSigneringDeltagareDTO) {
        super();
        this.ansvarigVirkeskopareId = dto?.ansvarigVirkeskopareId;
        this.ansvarigVirkeskopareNamn = dto?.ansvarigVirkeskopareNamn;
        this.avvisatMeddelande = dto?.avvisatMeddelande;
        this.avvisatTidpunkt = dto?.avvisatTidpunkt;
        this.id = dto?.id;
        this.leverantorId = dto?.leverantorId;
        this.leverantorNamn = dto?.leverantorNamn;
        this.makeMakaNamn = dto?.makeMakaNamn;
        this.makeMakaEpost = dto?.makeMakaEpost;
        this.signeringsmetod = dto?.signeringsmetod;
        this.smsNummer = dto?.smsNummer;
        this.deltagarTyp = dto?.deltagarTyp;
        this.senastVisat = dto?.senastVisat;
        this.signerat = dto?.signerat;
        this.signingProviderParticipantKey = dto?.signingProviderParticipantKey;
        this.status = dto?.status;
        this.emailDeliveryError = dto?.emailDeliveryError;
        this.alternativKontaktperson = dto?.alternativKontaktperson;
        this.namn = dto?.namn;
        this.inbjudningsOrdning =dto?.inbjudningsOrdning;
    }

}

export class SigneringHistorikData {
    signeringar: VirkeskopskontraktDigitalSigneringViewModel[];
}
